import { useEffect, useMemo, useRef } from 'react';
import { matchPath } from 'react-router-dom';

import Intercom, { onShow, showArticle, showSpace } from '@intercom/messenger-js-sdk';
import { InitType } from '@intercom/messenger-js-sdk/dist/types';
import { isFeatureEnabled } from 'common/utils';
import { useAuthorization } from 'modules/authorization/hooks';
import {
  INTERCOM_HIDDEN_ROUTES as CORE_HIDDEN_ROUTES,
  INTERCOM_PUBLIC_ROUTES as CORE_PUBLIC_ROUTES,
  INTERCOM_ARTICLE_MAP,
} from 'modules/router/Routes';
import { selectPathname } from 'modules/router/state/router.selectors';
import {
  INTERCOM_HIDDEN_ROUTES as NATTERS_HIDDEN_ROUTES,
  INTERCOM_PUBLIC_ROUTES as NATTERS_PUBLIC_ROUTES,
} from 'natters/routes';
import {
  INTERCOM_HIDDEN_ROUTES as NATALIE_HIDDEN_ROUTES,
  INTERCOM_PUBLIC_ROUTES as NATALIE_PUBLIC_ROUTES,
} from 'pages/Natalie/routes';
import { useAppSelector } from 'store';
import { useGetMessengerTokenQuery } from 'store/apis/intercom';
import { selectUserAccount } from 'user/api';

// Combine hidden routes from all modules
const INTERCOM_HIDDEN_ROUTES: readonly string[] = [
  ...CORE_HIDDEN_ROUTES,
  ...NATTERS_HIDDEN_ROUTES,
  ...NATALIE_HIDDEN_ROUTES,
];

// Combine public routes from all modules (for anonymous Intercom)
const INTERCOM_PUBLIC_ROUTES: readonly string[] = [
  ...CORE_PUBLIC_ROUTES,
  ...NATTERS_PUBLIC_ROUTES,
  ...NATALIE_PUBLIC_ROUTES,
];

export const useIntercom = () => {
  const userAccount = useAppSelector(selectUserAccount);
  const pathname = useAppSelector(selectPathname);
  const { isCommunityAdmin } = useAuthorization();
  const isOnShowRegistered = useRef(false);

  const userId = userAccount?.id?.toString();

  // Fetch JWT from backend - skip if no user
  const { data: tokenData } = useGetMessengerTokenQuery(undefined, {
    skip: !userId,
  });

  const isOnHiddenRoute = INTERCOM_HIDDEN_ROUTES.some((route) => matchPath(pathname, { path: route }));
  const isOnPublicRoute = INTERCOM_PUBLIC_ROUTES.some((route) => matchPath(pathname, { path: route }));

  const intercomData = useMemo<InitType | null>(() => {
    const isIntercomEnabled = isFeatureEnabled('IntercomEnabled');

    if (!isIntercomEnabled) {
      return null;
    }

    // Authenticated user path
    if (userAccount && tokenData?.token) {
      // Show launcher only to community admins and not on hidden routes
      const shouldHideLauncher = !isCommunityAdmin || isOnHiddenRoute;

      return {
        app_id: window._env_.INTERCOM_ID,
        intercom_user_jwt: tokenData.token,
        name: userAccount.displayName,
        hide_default_launcher: shouldHideLauncher,
        alignment: 'right',
        horizontal_padding: 12,
        vertical_padding: 12,
        avatar: {
          type: 'avatar',
          image_url: userAccount.avatarLink,
        },
      };
    }

    // Anonymous user path - only show on designated public routes
    if (isOnPublicRoute) {
      return {
        app_id: window._env_.INTERCOM_ID,
        hide_default_launcher: false,
        alignment: 'right',
        horizontal_padding: 12,
        vertical_padding: 12,
      };
    }

    return null;
  }, [userAccount, tokenData, isCommunityAdmin, isOnHiddenRoute, isOnPublicRoute]);

  useEffect(() => {
    if (!intercomData) {
      return;
    }

    Intercom(intercomData);

    // Register onShow callback only once after Intercom is initialized
    if (!isOnShowRegistered.current) {
      isOnShowRegistered.current = true;
      onShow(() => {
        const currentPath = window.location.pathname;
        for (const [pattern, articleId] of Object.entries(INTERCOM_ARTICLE_MAP)) {
          if (matchPath(currentPath, { path: pattern })) {
            showArticle(articleId);
            return;
          }
        }
        // No article mapping found - show home view to prevent cached article
        showSpace('home');
      });
    }
  }, [intercomData]);
};
